// Body of the tempurpedic ContactFormPage FORM state: header_content Toolbox
// stream, the contact section ("Contact Us" <h1>, rich-text intro, error
// banner, the contact form), the hardcoded info aside (+ the ChatCard island,
// its host div kept -- the aside CSS keys on `aside > div`), and the
// footer_content Toolbox stream. The two Toolbox streams render through the
// shared toolbox page map with tagName={React.Fragment} (no per-block
// wrapper).
//
// The form is rendered natively in React from the reactivated-serialized
// ContactFormRenderable; the captcha is the standalone
// <ReactivatedReCaptchaField> (excluded from serialization; CaptchaLibrary
// handles the client-side script injection). All translatable copy arrives
// pre-localized as string props (server-side gettext) so fr_CA is preserved;
// the submit "Submit" and the "Contact Us" <h1> are client-side ttag strings.
import { Immutable } from "immer";
import React from "react";
import { FormErrorList } from "tsi-common-react/src/forms/FormErrorList/index";
import { ReactivatedFormWidget } from "tsi-common-react/src/forms/ReactivatedFormWidget/index";
import {
    ReactivatedReCaptchaField,
    useReCaptchaSubmit,
} from "tsi-common-react/src/forms/ReactivatedReCaptcha/index";
import {
    FORM_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";
import { t } from "ttag";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { buildToolboxPageMap } from "../../blocks/toolboxPageMap";
import { ChatCard } from "../ChatCard";
import { ContactUsAside } from "../ContactUsAside";
import { useContactFormEnhancements } from "../useContactFormEnhancements";

type IProps = Immutable<templates.ContactFormPageTemplate>;

const REQUIRED_FIELD_CLASS_NAME = concatClassNames([
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
]);

const ContactForm = (
    props: Pick<
        IProps,
        "form" | "recaptcha_action" | "error_banner_text" | "captcha_errors"
    >,
): React.JSX.Element => {
    const { form } = props;
    const onSubmit = useReCaptchaSubmit(props.recaptcha_action);
    const { formRef, messageLabel, onReasonsChange } =
        useContactFormEnhancements(form);
    // The captcha's errors ride a side-channel (the field is excluded from
    // the serialized form), so a captcha-only failure must still raise the
    // banner.
    const hasErrors = Boolean(form.errors) || props.captcha_errors.length > 0;
    return (
        <>
            {hasErrors && (
                <div className="contact-form__errors-title">
                    {props.error_banner_text}
                </div>
            )}
            {/* `js-kuid-form` is a live analytics contract (kuidListeners
                binds submit listeners on it); `novalidate` defers validation
                to the server. */}
            <form
                action=""
                method="post"
                className={concatClassNames([
                    FORM_CLASS_NAME,
                    "form--contact",
                    "js-kuid-form",
                ])}
                noValidate
                ref={formRef}
                onSubmit={onSubmit}
                onChange={onReasonsChange}
            >
                <CSRFToken />
                {form.iterator.map((name) => {
                    const field = form.fields[name];
                    const errors = form.errors?.[name];
                    return (
                        <div
                            key={name}
                            className={
                                field.widget.required
                                    ? REQUIRED_FIELD_CLASS_NAME
                                    : FORM_FIELD_CLASS_NAME
                            }
                        >
                            <label htmlFor={field.widget.attrs.id}>
                                {name === "message" && messageLabel !== null
                                    ? messageLabel
                                    : field.label}
                            </label>
                            <ReactivatedFormWidget widget={field.widget} />
                            {errors && errors.length > 0 && (
                                <FormErrorList
                                    id={`${name.toLowerCase()}-errors`}
                                    errors={errors}
                                />
                            )}
                        </div>
                    );
                })}
                {/* Last form <div>: .form--contact's `> div:last-of-type`
                    centering + label clipping key on that position. */}
                <ReactivatedReCaptchaField
                    fieldClassName={REQUIRED_FIELD_CLASS_NAME}
                    errors={props.captcha_errors}
                />
                <span>
                    <input className="button" type="submit" value={t`Submit`} />
                </span>
            </form>
        </>
    );
};

export const ContactFormPageBody = (props: IProps): React.JSX.Element => {
    const toolboxMap = buildToolboxPageMap({
        hardcoded_ssr: props.hardcoded_ssr,
        hotspot_ssr: props.hotspot_ssr,
        breadcrumbs_ssr: props.breadcrumbs_ssr,
        customer_service_ssr: props.customer_service_ssr,
        shop_compare_ssr: props.shop_compare_ssr,
        tab_feature_attribute_ssr: props.tab_feature_attribute_ssr,
        reviews_ssr: props.reviews_ssr,
    });
    return (
        <div className="l-capped-width">
            <StreamField
                tagName={React.Fragment}
                value={props.page.header_content.value}
                components={toolboxMap}
            />

            <div className="contact-us-container">
                <section>
                    <header>
                        <h1>{t`Contact Us`}</h1>
                    </header>

                    <div className="intro-text">
                        <div
                            className="rich-text"
                            dangerouslySetInnerHTML={{
                                __html: props.intro_html,
                            }}
                        />
                    </div>

                    <ContactForm
                        form={props.form}
                        recaptcha_action={props.recaptcha_action}
                        error_banner_text={props.error_banner_text}
                        captcha_errors={props.captcha_errors}
                    />
                </section>

                <aside>
                    <ContactUsAside
                        aside={props.aside}
                        phoneVariant="heading"
                    />
                    {/* The ChatCard island's host div is load-bearing:
                        contact-us.scss lays the aside out per `aside > div`
                        child (grid columns, icon sizing), so the wrapper
                        div stays now the island is inlined. */}
                    <div>
                        <ChatCard />
                    </div>
                </aside>
            </div>

            <StreamField
                tagName={React.Fragment}
                value={props.page.footer_content.value}
                components={toolboxMap}
            />
        </div>
    );
};
